import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Input,
  Modal,
  Progress,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import type { PathwayStepProgress } from '../../schema/be-graphql-generated';
import {
  PATHWAY_PROGRESS_FIELDS,
  doneStepsOf,
  openStepsOf,
  stepStateColor,
  type PathwayProgressRow,
} from './model';
import { usePathwayCommand } from './usePathwayCommand';

/**
 * E34 · PathwayProgress — "Client Hub — Pathway progress" (cc-homework, chapter "Pathways — Define & enroll
 * (portal, E34)", screen e34e0004-0001-4000-8000-000000000025): one enrolment — the steps in their sections
 * with locked · released · done · skipped, the release and completion instants, the completion percentage;
 * Release on a manual step ("… — Release next step (section)", e34e0004-…-0020) and Withdraw ("… — Withdraw
 * (section)", e34e0004-…-0030) are its sections. `/pathways/progress/<enrollmentId>`, opened from the client
 * hub's Pathways and the Homework board's pathway cell.
 *
 * Behavior lines implemented here: i18n pathways.progress.*; gesture Release → ReleasePathwayStep (a manual
 * step, "Release makes it an ordinary assignment … due = release + the step's dueDays"), i18n
 * pathways.progress.release; gesture Withdraw → confirm with reason → WithdrawFromPathway ("its open step
 * assignments are cancelled, done ones stay done"), i18n pathways.withdraw.*. The phone's Program screen
 * renders the same read (block 3b). A coded refusal is shown verbatim.
 */
export const PathwayProgressPage = () => {
  const t = useTranslate();
  const { i18n } = useTranslation();
  const { enrollmentId } = useParams();
  const { message } = AntdApp.useApp();
  const { run } = usePathwayCommand();
  const [releasing, setReleasing] = useState<PathwayStepProgress | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading, isError, refetch } = useCustom<PathwayProgressRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: Boolean(enrollmentId) },
    meta: {
      operation: 'pathwayProgress',
      fields: PATHWAY_PROGRESS_FIELDS,
      variables: { enrollmentId: { name: 'enrollmentId', type: 'ID!', value: enrollmentId } },
    },
  });
  // a null answer is "not yours / not found" (portal conventions: null is a refusal, never a row)
  const p = (data?.data ?? null) as PathwayProgressRow | null;
  const dateLocale = i18n.languages[0]?.startsWith('ru') ? ru : enUS;
  const when = (iso: string | null | undefined, withTime = false) =>
    iso ? format(parseISO(iso), withTime ? 'd MMM yyyy, HH:mm' : 'd MMM yyyy', { locale: dateLocale }) : '—';

  if (isLoading) return <Spin />;
  if (isError || !p) {
    return <Alert type="error" showIcon message={t('pathways.progress.notFound')} data-testid="pathway-progress-not-found" />;
  }
  const running = p.status === 'running';
  const hubHref = `/clients/show/${encodeURIComponent(p.subject)}?tab=homework`;
  type StepRow = PathwayStepProgress & { section: string };
  const rows: StepRow[] = p.sections.flatMap((s) =>
    s.steps.map((st): StepRow => ({ ...st, section: s.section })),
  );

  const releaseLabel = (st: PathwayStepProgress) => {
    switch (st.release.rule) {
      case 'onDay':
        return t('pathways.release.onDayN', { day: st.release.day ?? 0 });
      case 'afterPrevious':
        return t('pathways.release.afterPreviousN', { count: st.release.delayDays ?? 0 });
      default:
        return t(`pathways.release.${st.release.rule}`);
    }
  };

  const release = async () => {
    if (!releasing) return;
    setBusy(true);
    const r = await run(
      'releasePathwayStep',
      { enrollmentId: p.enrollmentId, stepId: releasing.stepId },
      t('pathways.progress.releaseFailed'),
    );
    setBusy(false);
    if (!r.ok) {
      setProblem(r.message);
      return;
    }
    message.success(t('pathways.progress.released', { title: releasing.title }));
    setReleasing(null);
    refetch();
  };

  const withdraw = async () => {
    setBusy(true);
    const r = await run(
      'withdrawFromPathway',
      { enrollmentId: p.enrollmentId, reason: reason.trim() || null },
      t('pathways.withdraw.failed'),
    );
    setBusy(false);
    if (!r.ok) {
      setProblem(r.message);
      return;
    }
    message.success(t('pathways.withdraw.done'));
    setWithdrawing(false);
    refetch();
  };

  const open = openStepsOf(p);
  const done = doneStepsOf(p);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="pathway-progress">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Typography.Title level={3} style={{ marginBottom: 0 }} data-testid="pathway-progress-name">
            {p.pathwayName}
          </Typography.Title>
          <Typography.Text type="secondary" data-testid="pathway-progress-meta">
            {t('pathways.progress.meta', {
              version: p.pathwayVersion,
              date: when(p.startAt),
            })}
            {' · '}
            <Tag color={p.status === 'running' ? 'blue' : p.status === 'completed' ? 'green' : 'default'} data-testid="pathway-progress-status">
              {t(`pathways.progress.status.${p.status}`)}
            </Tag>
          </Typography.Text>
          {p.requesterName ? (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('pathways.progress.enrolledBy', { name: p.requesterName })}
              </Typography.Text>
            </div>
          ) : p.selfEnrolled ? (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }} data-testid="pathway-progress-self">
                {t('pathways.progress.selfEnrolled')}
              </Typography.Text>
            </div>
          ) : null}
        </div>
        <Space>
          <Link to={hubHref}>
            <Button data-testid="pathway-progress-back">{t('pathways.progress.back')}</Button>
          </Link>
          {running && !p.selfEnrolled ? (
            <Button
              danger
              onClick={() => {
                setReason('');
                setProblem(null);
                setWithdrawing(true);
              }}
              data-testid="pathway-progress-withdraw"
            >
              {t('pathways.withdraw.action')}
            </Button>
          ) : null}
        </Space>
      </Space>

      <Space direction="vertical" size={4} style={{ width: '100%', maxWidth: 480 }}>
        <Typography.Text data-testid="pathway-progress-summary">
          {t('pathways.progress.summary', { done: p.stepsDone + p.stepsSkipped, total: p.stepsTotal, percent: p.percentDone })}
        </Typography.Text>
        <Progress percent={p.percentDone} size="small" data-testid="pathway-progress-bar" />
      </Space>

      {p.status === 'withdrawn' && p.reason ? (
        <Alert type="info" showIcon message={t('pathways.progress.withdrawnReason', { reason: p.reason })} />
      ) : null}
      {problem && !releasing && !withdrawing ? (
        <Alert type="error" showIcon message={problem} data-testid="pathway-progress-problem" />
      ) : null}

      <Table<StepRow>
        rowKey="stepId"
        size="middle"
        pagination={false}
        dataSource={rows}
        onRow={(r) => ({ 'data-testid': `pathway-step-${r.stepId}`, 'data-state': r.state } as never)}
        columns={[
          { title: t('pathways.progress.column.section'), width: 200, render: (_, r) => r.section },
          {
            title: t('pathways.progress.column.step'),
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text>{r.title}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t(`homework.kind.${r.kind}`)}
                </Typography.Text>
              </Space>
            ),
          },
          { title: t('pathways.progress.column.rule'), width: 150, render: (_, r) => releaseLabel(r) },
          {
            title: t('pathways.progress.column.state'),
            width: 150,
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Tag color={stepStateColor(r.state)} data-testid={`pathway-step-state-${r.stepId}`}>
                  {t(`pathways.progress.state.${r.state}`)}
                </Tag>
                {r.cancelled ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('pathways.progress.cancelled')}
                  </Typography.Text>
                ) : null}
                {r.state === 'locked' && r.releaseAt ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }} data-testid={`pathway-step-release-at-${r.stepId}`}>
                    {t('pathways.progress.releaseAt', { date: when(r.releaseAt, true) })}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: t('pathways.progress.column.releasedAt'),
            width: 130,
            render: (_, r) => <span data-testid={`pathway-step-released-at-${r.stepId}`}>{when(r.releasedAt)}</span>,
          },
          {
            title: t('pathways.progress.column.outcomeAt'),
            width: 130,
            render: (_, r) => when(r.outcomeAt),
          },
          {
            title: '',
            width: 120,
            render: (_, r) =>
              running && r.state === 'locked' && r.release.rule === 'manual' && !p.selfEnrolled ? (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    setProblem(null);
                    setReleasing(r);
                  }}
                  data-testid={`pathway-step-release-${r.stepId}`}
                >
                  {t('pathways.progress.release')}
                </Button>
              ) : null,
          },
        ]}
      />

      <Modal
        open={!!releasing}
        title={t('pathways.progress.releaseTitle', { title: releasing?.title ?? '', stepId: releasing?.stepId ?? '' })}
        okText={t('pathways.progress.release')}
        cancelText={t('pathways.progress.notYet')}
        okButtonProps={{ loading: busy, 'data-testid': 'pathway-release-confirm' } as never}
        onOk={release}
        onCancel={() => setReleasing(null)}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} data-testid="pathway-release-dialog">
          <Typography.Paragraph>
            {releasing?.dueDays === null || releasing?.dueDays === undefined
              ? t('pathways.progress.releaseBodyNoDue')
              : t('pathways.progress.releaseBody', { count: releasing.dueDays })}
          </Typography.Paragraph>
          {problem ? <Alert type="error" showIcon message={problem} data-testid="pathway-release-error" /> : null}
        </Space>
      </Modal>

      <Modal
        open={withdrawing}
        title={t('pathways.withdraw.title', { name: p.pathwayName })}
        okText={t('pathways.withdraw.confirm')}
        cancelText={t('pathways.withdraw.keep')}
        okButtonProps={{ danger: true, loading: busy, 'data-testid': 'pathway-withdraw-confirm' } as never}
        onOk={withdraw}
        onCancel={() => setWithdrawing(false)}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} data-testid="pathway-withdraw-dialog">
          <Typography.Paragraph data-testid="pathway-withdraw-body">
            {t('pathways.withdraw.openSteps', { count: open.length })}{' '}
            {t('pathways.withdraw.doneSteps', { count: done.length })}
          </Typography.Paragraph>
          <Input.TextArea
            value={reason}
            maxLength={1000}
            autoSize={{ minRows: 2, maxRows: 5 }}
            placeholder={t('pathways.withdraw.reasonPlaceholder')}
            onChange={(e) => setReason(e.target.value)}
            data-testid="pathway-withdraw-reason"
          />
          {problem ? <Alert type="error" showIcon message={problem} data-testid="pathway-withdraw-error" /> : null}
        </Space>
      </Modal>
    </Space>
  );
};
